// src/pages/ActiveSessions.jsx
import React, { useEffect, useState, useCallback, useMemo, useContext } from "react";
import {
  Box,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Typography,
  CircularProgress,
  Chip,
  Stack,
  IconButton,
  Tooltip,
  TablePagination,
  TextField,
  InputAdornment,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Menu,
  MenuItem,
  Divider,
  ListItem,
  ListItemAvatar,
  Avatar,
  ListItemText,
  Checkbox,
  Breadcrumbs
} from "@mui/material";
import RefreshIcon from "@mui/icons-material/Refresh";
import SearchIcon from "@mui/icons-material/Search";
import MoreHorizIcon from "@mui/icons-material/MoreHoriz";
import HistoryIcon from "@mui/icons-material/History";
import { format, formatDistanceToNow } from "date-fns";
import { request } from "../utils/request";
import { Link as RouterLink } from "react-router-dom";
import CommonDeleteModal from '../components/Common/CommonDeleteModal';

import { ColorModeContext } from "../index";

export default function ActiveSessions() {
  const { mode } = useContext(ColorModeContext);
  const isDark = mode === 'dark';

  const currentUser = JSON.parse(localStorage.getItem('user') || '{}');
  const isSuperAdmin = currentUser.superadmin === true;
  const userPermissions = currentUser.permissions || [];

  const checkPerm = (action) => {
    if (isSuperAdmin) return true;
    return userPermissions.some((permission) => {
      const normalizedPermission = String(permission).toLowerCase();
      return (
        normalizedPermission.includes('active sessions') &&
        normalizedPermission.includes(action.toLowerCase())
      );
    });
  };

  const canDelete = checkPerm('delete');

  const [sessions, setSessions] = useState([]);
  const [usersMap, setUsersMap] = useState(new Map());
  const [loading, setLoading] = useState(false);
  const [lastFetchedAt, setLastFetchedAt] = useState(null);

  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(25);
  const [totalRows, setTotalRows] = useState(0);

  const [searchText, setSearchText] = useState("");
  const [debouncedSearch, setDebouncedSearch] = useState("");

  useEffect(() => {
    const t = setTimeout(() => setDebouncedSearch(searchText.trim()), 400);
    return () => clearTimeout(t);
  }, [searchText]);

  const [menuAnchorEl, setMenuAnchorEl] = useState(null);
  const [menuSessionId, setMenuSessionId] = useState(null);

  const [selectedIds, setSelectedIds] = useState([]);
  const [bulkDeleteOpen, setBulkDeleteOpen] = useState(false);
  const [bulkDeleting, setBulkDeleting] = useState(false);

  const parseRespToArray = (resp) => {
    if (!resp) return [];
    if (Array.isArray(resp)) return resp;
    if (Array.isArray(resp.data)) return resp.data;
    if (Array.isArray(resp.rows)) return resp.rows;
    if (resp.success && Array.isArray(resp.data)) return resp.data;
    return [];
  };

  const normalizeLoginTimes = (raw) => {
    if (!raw) return [];
    let arr = [];

    const pushVal = (v) => {
      if (v == null) return;
      if (typeof v === "object") {
        if (v.$date) arr.push(String(v.$date));
        else if (v.date) arr.push(String(v.date));
        else {
          if (v.createdAt) arr.push(String(v.createdAt));
          else if (v.updatedAt) arr.push(String(v.updatedAt));
          else arr.push(JSON.stringify(v));
        }
        return;
      }
      if (v instanceof Date) arr.push(v.toISOString());
      else arr.push(String(v));
    };

    if (Array.isArray(raw)) {
      for (const v of raw) pushVal(v);
    } else if (typeof raw === "object") {
      if (raw.loginTime) pushVal(raw.loginTime);
      if (raw.loginTimes) pushVal(raw.loginTimes);
      if (raw.createdAt) pushVal(raw.createdAt);
      if (raw.updatedAt) pushVal(raw.updatedAt);
      if (arr.length === 0) pushVal(raw);
    } else {
      pushVal(raw);
    }

    arr = arr
      .map((x) => (x == null ? null : String(x)))
      .filter(Boolean)
      .map((x) => {
        if (/^\d{10,}$/.test(x)) {
          const n = Number(x);
          try {
            return new Date(n).toISOString();
          } catch {
            return x;
          }
        }
        return x;
      });

    const seen = new Set();
    const deduped = [];
    for (const a of arr) {
      if (!seen.has(a)) {
        seen.add(a);
        deduped.push(a);
      }
    }

    deduped.sort((a, b) => {
      const ta = new Date(a).getTime();
      const tb = new Date(b).getTime();
      if (Number.isNaN(ta) || Number.isNaN(tb)) return 0;
      return tb - ta;
    });

    return deduped;
  };

  const normalizeSession = (s) => {
    const id = s && (s._id || s.id) ? String(s._id || s.id) : null;
    const sessionId = s.sessionId || s.session_id || s.session || "";
    const loginCandidates = [].concat(
      s.loginTime ?? [],
      s.loginTimes ?? [],
      s.login_time ?? [],
      s.login_times ?? [],
      s.createdAt ? [s.createdAt] : [],
      s.created_at ? [s.created_at] : []
    );
    const loginTimes = normalizeLoginTimes(loginCandidates.length ? loginCandidates : s.loginTime ?? s.loginTimes ?? s);
    const latest = loginTimes.length > 0 ? loginTimes[0] : null;

    const userId = s.userId || s.user_id || s.user || null;
    const ip = s.ip || s.clientIp || "";
    const deviceId =
      (s.devicePosture && (s.devicePosture.deviceId || s.devicePosture.deviceID)) ||
      s.deviceId ||
      (s.device_posture && (s.device_posture.deviceId || s.device_posture.deviceID)) ||
      "";
    const expiresAt = s.expiresAt || s.expires_at || null;

    return {
      _id: id,
      sessionId,
      loginTime: latest,
      loginTimes,
      userId: userId ? String(userId) : null,
      ip,
      deviceId,
      expiresAt,
      raw: s,
    };
  };

  const fetchUsers = useCallback(async () => {
    try {
      const usersResp = await request("user", { method: "GET" });
      const usersArray = parseRespToArray(usersResp);
      const uMap = new Map();
      for (const u of usersArray) {
        const id = u && (u._id || u.id) ? String(u._id || u.id) : null;
        if (!id) continue;
        const fname = u.first_name || "";
        const lname = u.last_name || "";
        const username = u.username || "";
        const display = [fname, lname].filter(Boolean).join(" ") || username || id;
        uMap.set(id, display);
      }
      setUsersMap(uMap);
    } catch (err) {
      console.error("ActiveSessions: failed to fetch users:", err);
    }
  }, []);

  const fetchSessionsPage = useCallback(
    async (pageIndex = 0, limit = 25, search = "") => {
      setLoading(true);
      try {
        const backendPage = Math.max(1, pageIndex + 1); 

        if (search) {
          try {
            const path = `sessions?page=1&limit=1000&search=${encodeURIComponent(search)}`;
            const resp = await request(path, { method: "GET" });
            const arr = parseRespToArray(resp);
            if (Array.isArray(arr) && arr.length > 0) {
              const normalized = arr.map((s) => normalizeSession(s));
              const t = search.toLowerCase();
              const filtered = normalized.filter((s) => {
                const sessionMatch = (s.sessionId || "").toLowerCase().includes(t);
                const userName = s.userId ? (usersMap.get(s.userId) || "").toLowerCase() : "";
                const userMatch = userName.includes(t);
                return sessionMatch || userMatch;
              });
              setSessions(filtered.slice(0, 1000)); 
              setTotalRows(filtered.length);
              setLastFetchedAt(new Date());
              return;
            }
          } catch (e) {
            console.debug("Server-side search failed, falling back to client-side search.", e?.message || e);
          }

          const fallbackPath = `sessions?page=1&limit=1000`;
          const fallbackResp = await request(fallbackPath, { method: "GET" });
          const fallbackArr = parseRespToArray(fallbackResp);
          const normalized = fallbackArr.map((s) => normalizeSession(s));
          const t = search.toLowerCase();
          const filtered = normalized.filter((s) => {
            const sessionMatch = (s.sessionId || "").toLowerCase().includes(t);
            const userName = s.userId ? (usersMap.get(s.userId) || "").toLowerCase() : "";
            const userMatch = userName.includes(t);
            return sessionMatch || userMatch;
          });
          setSessions(filtered.slice(0, 1000));
          setTotalRows(filtered.length);
          setLastFetchedAt(new Date());
          return;
        }

        const path = `sessions?page=${backendPage}&limit=${limit}`;
        const resp = await request(path, { method: "GET" });

        if (resp && typeof resp === "object" && Array.isArray(resp.data) && resp.meta) {
          const normalized = resp.data.map((s) => normalizeSession(s));
          setSessions(normalized);
          const total =
            Number(resp.meta.total ?? resp.meta.totalCount ?? resp.meta.count ?? resp.meta.totalDocs ?? resp.total ?? 0);
          setTotalRows(total);
        } else {
          const arr = parseRespToArray(resp);
          const normalized = arr.map((s) => normalizeSession(s));
          setSessions(normalized);
          setTotalRows(arr.length || 0);
        }

        setLastFetchedAt(new Date());
      } catch (err) {
        console.error("ActiveSessions: failed to fetch sessions:", err);
        setSessions([]);
        setTotalRows(0);
      } finally {
        setLoading(false);
      }
    },
    [usersMap]
  );

  useEffect(() => {
    fetchUsers();
  }, [fetchUsers]);

  useEffect(() => {
    if (debouncedSearch) {
      setPage(0);
      fetchSessionsPage(0, rowsPerPage, debouncedSearch);
    } else {
      fetchSessionsPage(page, rowsPerPage, "");
    }
  }, [page, rowsPerPage, debouncedSearch, fetchSessionsPage]);

  const handleChangePage = (event, newPage) => {
    setPage(newPage);
  };

  const handleChangeRowsPerPage = (event) => {
    const newLimit = parseInt(event.target.value, 10);
    setRowsPerPage(newLimit);
    setPage(0);
  };

  const renderStatusChip = (expiresAt) => {
    const active = !expiresAt;
    return active ? (
      <Box
        sx={{
          display: 'inline-block',
          bgcolor: 'var(--blue-tint)',
          color: 'var(--edge-blue)',
          borderRadius: '4px',
          px: 1,
          py: 0.25,
          fontFamily: 'Calibri',
          fontSize: '12px',
          whiteSpace: 'nowrap'
        }}
      >
        Active
      </Box>
    ) : (
      <Box
        sx={{
          display: 'inline-block',
          bgcolor: 'rgba(214, 50, 58, 0.1)',
          color: 'var(--critical)',
          borderRadius: '4px',
          px: 1,
          py: 0.25,
          fontFamily: 'Calibri',
          fontSize: '12px',
          whiteSpace: 'nowrap'
        }}
      >
        Inactive
      </Box>
    );
  };

  const fmt = (iso) => {
    if (!iso) return "—";
    try {
      const d = new Date(iso);
      if (Number.isNaN(d.getTime())) return String(iso);
      return format(d, "yyyy-MM-dd HH:mm:ss");
    } catch {
      return String(iso);
    }
  };

  const refresh = async () => {
    await fetchUsers();
    await fetchSessionsPage(page, rowsPerPage, debouncedSearch);
  };

  const effectiveTotal = useMemo(() => totalRows || 0, [totalRows]);

  const toggleSelect = (id) => {
    setSelectedIds((prev) => (prev.includes(id) ? prev.filter((x) => x !== id) : [...prev, id]));
  };

  const isAllSelected = sessions.length > 0 && selectedIds.length === sessions.length;

  const toggleSelectAll = (checked) => {
    if (checked) {
      setSelectedIds(sessions.map((s) => s._id || s.sessionId));
    } else {
      setSelectedIds([]);
    }
  };

  const handleDeleteSelected = () => {
    if (!canDelete) return;
    if (selectedIds.length === 0) return;
    setBulkDeleteOpen(true);
  };

  const handleCancelBulkDelete = () => {
    setBulkDeleteOpen(false);
    setBulkDeleting(false);
  };

  const handleConfirmBulkDelete = async () => {
    if (selectedIds.length === 0) return;
    setBulkDeleting(true);
    try {
      await Promise.all(
        selectedIds.map((id) => {
          return request(`sessions/${id}`, { method: "DELETE" }).catch((e) => {
            console.error("Failed to delete session", id, e);
          });
        })
      );
      setSelectedIds([]);
      await fetchSessionsPage(page, rowsPerPage, debouncedSearch);
      handleCancelBulkDelete();
    } catch (err) {
      console.error("Bulk delete failed:", err);
      setBulkDeleting(false);
    }
  };

  const openMenuFor = (event, sessionId) => {
    setMenuAnchorEl(event.currentTarget);
    setMenuSessionId(sessionId);
  };

  const closeMenu = () => {
    setMenuAnchorEl(null);
    setMenuSessionId(null);
  };

  const getLoginTimesForSession = (s) => {
    if (!s) return [];
    if (Array.isArray(s.loginTimes) && s.loginTimes.length) return s.loginTimes;
    if (s.loginTime) return [s.loginTime];
    return [];
  };

  return (
    <Box p={3} sx={{ minHeight: '100vh', bgcolor: 'var(--mist)' }}>
      <Stack direction="row" justifyContent="space-between" alignItems="center" mb={2} spacing={2}>
        <Box sx={{ display: "flex", alignItems: "center", gap: 1, flex: "0 1 auto" }}>
          <Breadcrumbs aria-label="breadcrumb" sx={{ mb: 1 }}>
            <RouterLink
              to="/home"
              style={{
                fontFamily: 'Calibri',
                fontSize: '16px',
                color: 'var(--edge-blue)',
                textDecoration: 'none',
              }}
              onMouseEnter={(e) => (e.currentTarget.style.textDecoration = 'underline')}
              onMouseLeave={(e) => (e.currentTarget.style.textDecoration = 'none')}
            >
              Dashboard
            </RouterLink>
            <Typography sx={{ fontFamily: 'Calibri', fontSize: '16px', fontWeight: 700, color: 'var(--vault-navy)', margin: 0 }}>
              Active Sessions
            </Typography>
          </Breadcrumbs>
        </Box>

        <Stack direction="row" spacing={1} alignItems="center" sx={{ flex: "1 1 auto", justifyContent: "flex-end" }}>
          <TextField
            size="small"
            placeholder="Search by session ID or username"
            value={searchText}
            onChange={(e) => setSearchText(e.target.value)}
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <SearchIcon fontSize="small" sx={{ color: 'var(--slate)' }} />
                </InputAdornment>
              ),
            }}
            sx={{
              minWidth: 260,
              '& .MuiOutlinedInput-root': {
                borderRadius: '6px',
                fontFamily: 'Calibri',
                fontSize: '16px',
                color: 'var(--vault-navy)',
                bgcolor: '#FFF',
                height: '40px',
                '& fieldset': {
                  borderColor: 'var(--hairline)',
                  borderWidth: '1px',
                },
                '&:hover fieldset': {
                  borderColor: 'var(--hairline)',
                },
                '&.Mui-focused fieldset': {
                  borderColor: 'var(--edge-blue)',
                  borderWidth: '2px',
                },
                '&.Mui-focused': {
                  boxShadow: '0 0 0 3px var(--blue-tint)',
                }
              },
              '& input::placeholder': {
                color: 'var(--slate)',
                opacity: 1,
              },
            }}
          />

          <Tooltip title="Refresh">
            <IconButton size="small" onClick={refresh} sx={{ color: 'var(--slate)', '&:hover': { color: 'var(--edge-blue)', bgcolor: 'transparent' } }}>
              <RefreshIcon fontSize="small" />
            </IconButton>
          </Tooltip>
        </Stack>
      </Stack>

      {selectedIds.length > 0 && (
        <Box mb={2} display="flex" justifyContent="flex-start" alignItems="center" gap={1}>
          <Typography sx={{ fontFamily: 'Calibri', fontSize: '16px', color: 'var(--vault-navy)', fontWeight: 700, mr: 1 }}>
            {selectedIds.length} selected
          </Typography>

          {canDelete && (
            <Button
              size="small"
              onClick={handleDeleteSelected}
              sx={{
                bgcolor: '#FFF',
                color: 'var(--critical)',
                fontFamily: 'Arial',
                fontWeight: 'bold',
                fontSize: '14px',
                borderRadius: '6px',
                height: '32px',
                border: '1px solid var(--critical)',
                textTransform: 'none',
                '&:hover:not(:disabled)': {
                  bgcolor: 'rgba(214, 50, 58, 0.04)',
                }
              }}
            >
              Delete
            </Button>
          )}

          <Button
            size="small"
            onClick={() => setSelectedIds([])}
            sx={{
              color: 'var(--edge-blue)',
              fontFamily: 'Arial',
              fontWeight: 'bold',
              fontSize: '14px',
              textTransform: 'none',
              '&:hover': {
                textDecoration: 'underline',
                bgcolor: 'transparent'
              }
            }}
          >
            Clear
          </Button>
        </Box>
      )}

      <Paper
        elevation={0}
        sx={{
          bgcolor: 'var(--canvas)',
          border: '1px solid var(--hairline)',
          borderRadius: '12px',
          overflow: 'hidden',
          display: 'flex',
          flexDirection: 'column',
          mb: 3,
          boxShadow: 'none',
          maxHeight: 'calc(100vh - 170px)',
        }}
      >
        <Box
          sx={{
            overflowX: 'auto',
            overflowY: 'auto',
            flexGrow: 1,
            width: '100%',
            scrollbarWidth: 'thin',
            scrollbarColor: 'var(--slate) transparent',
            '&::-webkit-scrollbar': { width: '6px', height: '6px' },
            '&::-webkit-scrollbar-track': { background: 'transparent' },
            '&::-webkit-scrollbar-thumb': { background: 'var(--slate)', borderRadius: '10px' },
          }}
        >
          <Box
            component="table"
            sx={{ 
              minWidth: 1000, 
              width: '100%', 
              borderCollapse: 'collapse',
              textAlign: 'left'
            }}
          >
            <Box component="thead" sx={{ bgcolor: '#FFF', borderBottom: '1px solid var(--hairline)' }}>
              <Box component="tr">
                <Box
                  component="th"
                  sx={{
                    p: '8px 16px',
                    width: 48,
                    textAlign: 'center',
                  }}
                >
                  <Checkbox 
                    checked={isAllSelected} 
                    indeterminate={selectedIds.length > 0 && !isAllSelected} 
                    onChange={(e) => toggleSelectAll(e.target.checked)} 
                    inputProps={{ 'aria-label': 'select all sessions' }}
                    sx={{
                      p: '4px',
                      color: 'var(--slate)',
                      '&.Mui-checked, &.MuiCheckbox-indeterminate': {
                        color: 'var(--edge-blue)',
                      },
                      '& .MuiSvgIcon-root': { fontSize: '20px', borderRadius: '4px' }
                    }}
                  />
                </Box>
                {[
                  { label: '#', align: 'left', width: '5%' },
                  { label: 'Session ID', align: 'left', width: '20%' },
                  { label: 'User', align: 'left', width: '15%' },
                  { label: 'IP', align: 'left', width: '15%' },
                  { label: 'Device ID', align: 'left', width: '15%' },
                  { label: 'Login Time', align: 'left', width: '15%' },
                  { label: 'Older Logins', align: 'center', width: '10%' },
                  { label: 'Status', align: 'center', width: '10%' }
                ].map((col, i) => (
                  <Box
                    component="th"
                    key={i}
                    textAlign={col.align}
                    sx={{
                      p: '8px 16px',
                      width: col.width,
                      color: 'var(--vault-navy)',
                      fontFamily: 'Arial, sans-serif',
                      fontSize: '13px',
                      fontWeight: 700,
                      whiteSpace: 'nowrap'
                    }}
                  >
                    {col.label}
                  </Box>
                ))}
              </Box>
            </Box>

            <Box component="tbody">
              {loading ? (
                <Box component="tr">
                  <Box component="td" colSpan={9} sx={{ py: 6, textAlign: 'center' }}>
                    <CircularProgress size={24} sx={{ color: 'var(--edge-blue)' }} />
                  </Box>
                </Box>
              ) : sessions.length === 0 ? (
                <Box component="tr">
                  <Box component="td" colSpan={9} sx={{ py: 4, textAlign: 'center', color: 'var(--slate)', fontFamily: 'Calibri', fontSize: '14px' }}>
                    No sessions found.
                  </Box>
                </Box>
              ) : (
                sessions.map((s, idx) => {
                  const rowNumber = page * rowsPerPage + idx + 1;
                  const loginTimes = getLoginTimesForSession(s);
                  const latest = loginTimes.length > 0 ? loginTimes[0] : null;
                  const olderTimes = loginTimes.length > 1 ? loginTimes.slice(1) : [];

                  return (
                    <Box 
                      component="tr" 
                      key={s._id || `${s.sessionId}-${idx}`} 
                      sx={{
                        bgcolor: '#FFF',
                        borderBottom: '1px solid var(--hairline)',
                        '&:hover': { bgcolor: 'var(--mist)' }
                      }}
                    >
                      <Box component="td" sx={{ p: '8px 16px', textAlign: 'center', verticalAlign: 'middle' }}>
                        <Checkbox 
                          checked={selectedIds.includes(s._id)} 
                          onChange={() => toggleSelect(s._id)} 
                          sx={{
                            p: '4px',
                            color: 'var(--slate)',
                            '&.Mui-checked': { color: 'var(--edge-blue)' },
                            '& .MuiSvgIcon-root': { fontSize: '20px', borderRadius: '4px' }
                          }}
                        />
                      </Box>

                      <Box component="td" sx={{ p: '8px 16px', verticalAlign: 'middle', fontFamily: 'Calibri', fontSize: '14px', color: 'var(--slate)' }}>
                        {rowNumber}
                      </Box>

                      <Box component="td" sx={{ p: '8px 16px', verticalAlign: 'middle', maxWidth: 240, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>
                        <Typography variant="body2" noWrap sx={{ fontFamily: 'Arial, sans-serif', fontSize: '14px', fontWeight: 600, color: 'var(--vault-navy)' }}>
                          {s.sessionId || "—"}
                        </Typography>
                      </Box>

                      <Box component="td" sx={{ p: '8px 16px', verticalAlign: 'middle', fontFamily: 'Calibri', fontSize: '14px', color: 'var(--slate)' }}>
                        {s.userId ? (usersMap.get(s.userId) || s.userId) : "—"}
                      </Box>

                      <Box component="td" sx={{ p: '8px 16px', verticalAlign: 'middle', fontFamily: 'Calibri', fontSize: '14px', color: 'var(--slate)' }}>
                        {s.ip || "—"}
                      </Box>
                      
                      <Box component="td" sx={{ p: '8px 16px', verticalAlign: 'middle', fontFamily: 'Calibri', fontSize: '14px', color: 'var(--slate)' }}>
                        {s.deviceId || "—"}
                      </Box>

                      <Box component="td" sx={{ p: '8px 16px', verticalAlign: 'middle' }}>
                        <Stack direction="column" spacing={0}>
                          <Typography variant="body2" sx={{ fontFamily: 'Calibri', fontSize: '14px', fontWeight: 600, color: 'var(--vault-navy)' }}>
                            {fmt(latest)}
                          </Typography>
                          <Typography variant="caption" sx={{ fontFamily: 'Calibri', fontSize: '12px', color: 'var(--slate)' }}>
                            {latest ? formatDistanceToNow(new Date(latest), { addSuffix: true }) : "—"}
                          </Typography>
                        </Stack>
                      </Box>

                      <Box component="td" sx={{ p: '8px 16px', verticalAlign: 'middle', textAlign: 'center' }}>
                        {olderTimes.length > 0 ? (
                          <Tooltip title={`Show ${olderTimes.length} older login(s)`}>
                            <IconButton
                              size="small"
                              aria-controls={menuSessionId === (s._id || s.sessionId) && Boolean(menuAnchorEl) ? `login-times-menu-${s._id || s.sessionId}` : undefined}
                              aria-haspopup="true"
                              aria-expanded={menuSessionId === (s._id || s.sessionId) && Boolean(menuAnchorEl) ? "true" : undefined}
                              onClick={(e) => openMenuFor(e, s._id || s.sessionId)}
                              sx={{
                                border: "1px solid var(--hairline)",
                                bgcolor: "#FFF",
                                color: 'var(--vault-navy)',
                                "&:hover": { bgcolor: "var(--mist)" },
                                ml: 0.5,
                              }}
                            >
                              <MoreHorizIcon fontSize="small" />
                            </IconButton>
                          </Tooltip>
                        ) : (
                          <Typography variant="caption" sx={{ fontFamily: 'Calibri', fontSize: '14px', color: 'var(--slate)' }}>
                            —
                          </Typography>
                        )}
                      </Box>

                      <Box component="td" sx={{ p: '8px 16px', verticalAlign: 'middle', textAlign: 'center' }}>
                        {renderStatusChip(s.expiresAt)}
                      </Box>
                    </Box>
                  );
                })
              )}
            </Box>
          </Box>
        </Box>
        
        <Box sx={{ borderTop: '1px solid var(--hairline)', bgcolor: '#FFF' }}>
          <TablePagination
            rowsPerPageOptions={[25, 50, 100]}
            component="div"
            count={effectiveTotal}
            rowsPerPage={rowsPerPage}
            page={page}
            onPageChange={handleChangePage}
            onRowsPerPageChange={handleChangeRowsPerPage}
            sx={{
              fontFamily: 'Calibri',
              color: 'var(--vault-navy)',
              '& .MuiIconButton-root': { color: 'var(--vault-navy)' },
              '& .MuiIconButton-root.Mui-disabled': { color: 'var(--slate)' },
            }}
          />
        </Box>
      </Paper>

      <Menu
        id={menuSessionId ? `login-times-menu-${menuSessionId}` : "login-times-menu"}
        anchorEl={menuAnchorEl}
        open={Boolean(menuAnchorEl) && Boolean(menuSessionId)}
        onClose={closeMenu}
        anchorOrigin={{ vertical: "bottom", horizontal: "left" }}
        transformOrigin={{ vertical: "top", horizontal: "left" }}
        PaperProps={{
          sx: {
            maxHeight: 360,
            minWidth: 320,
            borderRadius: 2,
            boxShadow: 6,
            p: 1,
            backgroundColor: isDark ? '#1e293b' : '#fff',
            color: isDark ? '#fff' : 'inherit'
          },
        }}
      >
        <ListItem dense sx={{ pointerEvents: "none", mb: 0.5 }}>
          <ListItemAvatar>
            <Avatar sx={{ bgcolor: isDark ? "rgba(255,255,255,0.1)" : "primary.main", color: isDark ? "#90caf9" : "#fff" }}>
              <HistoryIcon />
            </Avatar>
          </ListItemAvatar>
          <ListItemText
            primary={<Typography variant="subtitle2">Older logins</Typography>}
            secondary={<Typography variant="caption" sx={{ color: isDark ? '#aaa' : "text.secondary" }}>Showing entries older than the latest (newest → oldest)</Typography>}
          />
        </ListItem>

        <Divider sx={{ my: 0.5, borderColor: isDark ? 'rgba(255,255,255,0.1)' : undefined }} />

        {(() => {
          if (!menuSessionId) return null;
          const s = sessions.find((it) => (it._id || it.sessionId) === menuSessionId);
          if (!s) return (
            <MenuItem dense>
              <Typography variant="body2">No entries</Typography>
            </MenuItem>
          );
          const loginTimes = getLoginTimesForSession(s);
          const older = loginTimes.length > 1 ? loginTimes.slice(1) : [];
          if (older.length === 0) {
            return (
              <MenuItem dense>
                <Typography variant="body2">No older logins</Typography>
              </MenuItem>
            );
          }
          return older.map((lt, i) => {
            const position = i + 2;
            let relative = "—";
            try {
              relative = formatDistanceToNow(new Date(lt), { addSuffix: true });
            } catch {
              /* ignore */
            }
            return (
              <MenuItem
                key={`${menuSessionId}-older-${i}`}
                dense
                sx={{
                  alignItems: "flex-start",
                  py: 1,
                  "&:hover": { bgcolor: isDark ? "rgba(255,255,255,0.05)" : "action.hover" },
                }}
              >
                <ListItemAvatar>
                  <Avatar sx={{ bgcolor: isDark ? "rgba(255,255,255,0.1)" : "grey.100", color: isDark ? "#fff" : "text.primary", width: 34, height: 34 }}>
                    <HistoryIcon sx={{ fontSize: 18 }} />
                  </Avatar>
                </ListItemAvatar>

                <ListItemText
                  primary={
                    <Stack direction="row" justifyContent="space-between" alignItems="center">
                      <Typography variant="body2">{fmt(lt)}</Typography>
                      <Chip size="small" label={`#${position}`} sx={{ ml: 1, bgcolor: isDark ? "rgba(255,255,255,0.1)" : "grey.100", color: isDark ? '#fff' : 'inherit', fontWeight: 600 }} />
                    </Stack>
                  }
                  secondary={<Typography variant="caption" sx={{ color: isDark ? '#aaa' : "text.secondary" }}>{relative}</Typography>}
                />
              </MenuItem>
            );
          });
        })()}
      </Menu>

      <CommonDeleteModal
        open={bulkDeleteOpen}
        onClose={handleCancelBulkDelete}
        onConfirm={handleConfirmBulkDelete}
        isDeleting={bulkDeleting}
        entityType="Session"
        isBulk={true}
        bulkCount={selectedIds.length}
      />
    </Box>
  );
}